import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { PageHeader, Panel, AllergenTags } from "@/components/ui-bits";
import { DailyTotalsPanel } from "@/components/DailyTotalsPanel";
import { Clock, Layers, PackageOpen } from "lucide-react";

/**
 * View modes:
 *   • "today"  — only packing rows produced inside the current shift day
 *                (the operator's "what came off the line today" view)
 *   • "all"    — every label with `remaining_packs > 0` (the full available
 *                inventory across all days — the default for dispatchers)
 */
export default function Inventory() {
  const { params, selected } = useLocations();
  const [rows, setRows] = useState([]);
  const [dailyTotals, setDailyTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [mode, setMode] = useState("all");

  useEffect(() => {
    setTotalsLoading(true);
    // Inventory itself stays at /inventory (full available stock); the
    // "today" view layers on a packing/?today=1 filter so the underlying
    // stock figures stay consistent.
    const invReq =
      mode === "today"
        ? api.get("/packing", { params: { ...params(), today: 1 } })
        : api.get("/inventory", { params: params() });
    Promise.all([invReq, api.get("/production/daily-totals", { params: params() })]).then(
      ([inv, eod]) => {
        setRows(inv.data);
        setDailyTotals(eod.data);
        setTotalsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, mode]);

  const total = rows.reduce(
    (s, r) =>
      s +
      Math.max(
        0,
        Number(r.packs_produced || 0) -
          Number(r.packing_waste || 0) -
          Number(r.unsold_packs || 0),
      ),
    0,
  );
  const todayProduced = rows.reduce((s, r) => s + (r.packs_produced || 0), 0);

  // Product-level rollup — the "at a glance" answer to "what's left unsold".
  // For "all-time" mode, we compute per-batch:
  //   sold_or_available = produced - waste - unsold
  // This matches how the bakery counts it: what came off the line, minus
  // anything binned or explicitly logged as unsold. Dispatch is intentionally
  // NOT subtracted here (per Oz Bakery: "inventory should not get the number
  // from dispatch — remaining is sold").
  const byProduct = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const produced = Number(r.packs_produced || 0);
      const waste = Number(r.packing_waste || 0);
      const unsold = Number(r.unsold_packs || 0);
      const qty =
        mode === "today" ? produced : Math.max(0, produced - waste - unsold);
      if (qty <= 0) continue;
      const key = r.recipe_id || r.recipe_name || "unknown";
      const cur = map.get(key) || {
        recipe_id: key,
        recipe_name: r.recipe_name || "—",
        pack_unit: r.pack_unit || "pack",
        qty: 0,
        waste: 0,
        unsold: 0,
        batches: 0,
        oldest: r.packed_at,
      };
      cur.qty += qty;
      cur.waste += waste;
      cur.unsold += unsold;
      cur.batches += 1;
      if (!cur.oldest || (r.packed_at && r.packed_at < cur.oldest)) {
        cur.oldest = r.packed_at;
      }
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty);
  }, [rows, mode]);

  return (
    <div>
      <PageHeader
        title="Finished Inventory"
        eyebrow={
          mode === "today"
            ? `Today · ${todayProduced} packs produced · ${total} sold`
            : `Sold · ${total} packs across ${rows.length} lots`
        }
      />
      <DailyTotalsPanel totals={dailyTotals} loading={totalsLoading} />

      <Panel
        title={
          mode === "today"
            ? `Produced today by product (${byProduct.length})`
            : `Sold by product · produced − waste − unsold (${byProduct.length})`
        }
        action={
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-500">
            <PackageOpen className="h-4 w-4" />
            {total} pack{total === 1 ? "" : "s"} total
          </span>
        }
      >
        {byProduct.length === 0 ? (
          <div className="py-8 text-center text-stone-500" data-testid="inv-by-product-empty">
            {mode === "today"
              ? "Nothing produced yet this shift."
              : "No production recorded yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inv-by-product-table">
              <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="py-3">Product</th>
                  <th className="text-right">
                    {mode === "today" ? "Produced" : "Sold"}
                  </th>
                  {mode === "all" && <th className="text-right">Waste</th>}
                  {mode === "all" && <th className="text-right">Unsold</th>}
                  <th className="text-right">Batches</th>
                  <th>Oldest packed</th>
                </tr>
              </thead>
              <tbody>
                {byProduct.map((p) => (
                  <tr
                    key={p.recipe_id}
                    className="border-t border-border h-14"
                    data-testid={`inv-by-product-${p.recipe_id}`}
                  >
                    <td className="font-medium">{p.recipe_name}</td>
                    <td className="text-right tnum font-bold text-lg">
                      {p.qty}
                      <span className="text-xs font-normal text-stone-500 ml-1">
                        {p.pack_unit}
                      </span>
                    </td>
                    {mode === "all" && (
                      <td className="text-right tnum text-rose-700">
                        {p.waste || "—"}
                      </td>
                    )}
                    {mode === "all" && (
                      <td className="text-right tnum text-amber-700">
                        {p.unsold || "—"}
                      </td>
                    )}
                    <td className="text-right tnum text-stone-600">{p.batches}</td>
                    <td className="text-stone-600 text-sm tnum">
                      {p.oldest?.slice(0, 10) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title={mode === "today" ? "Produced today" : "Sold by label"}
        action={
          <div
            className="inline-flex h-10 rounded-lg border border-border overflow-hidden bg-stone-50 dark:bg-stone-800"
            role="tablist"
            data-testid="inventory-mode"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "today"}
              onClick={() => setMode("today")}
              data-testid="inventory-mode-today"
              className={`px-3 h-10 text-sm font-medium inline-flex items-center gap-1.5 transition ${
                mode === "today"
                  ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white"
                  : "text-stone-600 hover:text-stone-900"
              }`}
            >
              <Clock className="h-4 w-4" /> Today
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "all"}
              onClick={() => setMode("all")}
              data-testid="inventory-mode-all"
              className={`px-3 h-10 text-sm font-medium inline-flex items-center gap-1.5 transition ${
                mode === "all"
                  ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white"
                  : "text-stone-600 hover:text-stone-900"
              }`}
            >
              <Layers className="h-4 w-4" /> All sold
            </button>
          </div>
        }
      >
        {rows.length === 0 ? (
          <div className="py-10 text-center text-stone-500">
            {mode === "today" ? "Nothing produced yet this shift." : "No sold stock recorded yet."}
          </div>
        ) : (
          <table className="w-full" data-testid="inventory-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Label</th>
                <th>Product</th>
                <th>Batch</th>
                <th>Allergens</th>
                <th className="text-right">Pack size</th>
                <th className="text-right">
                  {mode === "today" ? "Produced" : "Sold"}
                </th>
                <th>Packed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const sold = Math.max(
                  0,
                  Number(p.packs_produced || 0) -
                    Number(p.packing_waste || 0) -
                    Number(p.unsold_packs || 0),
                );
                return (
                  <tr key={p.id} className="border-t border-border h-16">
                    <td className="font-mono text-sm">{p.label_code}</td>
                    <td className="font-medium">{p.recipe_name}</td>
                    <td className="font-mono text-sm text-stone-600">{p.batch_code}</td>
                    <td><AllergenTags items={p.allergens} /></td>
                    <td className="text-right tnum">{fmt2(p.pack_size)} {p.pack_unit}</td>
                    <td className="text-right tnum font-semibold">
                      {fmt2(mode === "today" ? p.packs_produced : sold)}
                    </td>
                    <td className="text-stone-600 text-sm">{p.packed_at?.slice(0,16).replace("T"," ")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
